/**
 * TeamCompetitionDetail — Phase 7 Sub-iter 7C.
 *
 * Wave 29 Chunk 9a in its EntityShell form: Overview / Leaderboard /
 * Activity tabs.  HR-only inline "Close competition" 2-step confirm
 * replaces the legacy `window.confirm` (Trust Contract Rule 2).
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Trophy, Edit3, CheckCircle, Loader2, Lock, Coins, Save,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import { EntityShell } from "../components/shell";
import HistoryBlock from "../components/shell/HistoryBlock";

export default function TeamCompetitionDetail() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { id } = useParams();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const [comp, setComp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [scoreEdits, setScoreEdits] = useState({});
  const [savingUid, setSavingUid] = useState(null);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/team/competitions/${id}`);
      setComp(data);
      setScoreEdits({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load competition.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user, id]);

  const doClose = async () => {
    setClosing(true);
    try {
      await api.post(`/team/competitions/${id}/close`);
      toast.success("Competition closed");
      setConfirmClose(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Close failed.");
    } finally {
      setClosing(false);
    }
  };

  const saveScore = async (uid) => {
    const edit = scoreEdits[uid];
    if (!edit || edit.value === "" || edit.value === null || edit.value === undefined) return;
    setSavingUid(uid);
    try {
      await api.post(`/team/competitions/${id}/score`, {
        user_id: uid,
        score: Number(edit.value),
        note: (edit.note || "").trim() || null,
      });
      toast.success("Score saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSavingUid(null);
    }
  };

  if (busy && !comp) {
    return (
      <div className="hv-card p-6 flex items-center gap-2 text-[#9aa3b5] text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!comp) return null;

  const isClosed = comp.status === "closed";
  const isManual = comp.metric_source === "manual";
  const rows = comp.leaderboard || [];

  const headerActions = (
    <>
      {isHr && !isClosed && (
        <button
          onClick={() => nav(`/team/competitions/${id}/edit`)}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="comp-edit-btn"
        >
          <Edit3 className="w-3.5 h-3.5" /> Edit
        </button>
      )}
      {isHr && !isClosed && !confirmClose && (
        <button
          onClick={() => setConfirmClose(true)}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="comp-close-btn"
        >
          <Lock className="w-3.5 h-3.5" /> Close
        </button>
      )}
      {isHr && !isClosed && confirmClose && (
        <>
          <button
            onClick={() => setConfirmClose(false)}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="comp-close-cancel"
          >
            Cancel
          </button>
          <button
            onClick={doClose}
            disabled={closing}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="comp-close-confirm"
            title={comp.auto_grant_points ? "Points will auto-grant to top 3." : "Final leaderboard will be locked."}
          >
            {closing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
            Confirm close
          </button>
        </>
      )}
    </>
  );

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "leaderboard", label: "Leaderboard", count: rows.length || undefined },
    { key: "activity", label: "Activity" },
  ];

  return (
    <EntityShell
      kindLabel="Competition"
      title={comp.name}
      status={{ label: comp.status, tone: isClosed ? "slate" : "green" }}
      backTo="/team/competitions"
      actions={headerActions}
      tabs={tabs}
      activeTab={tab}
      onTabChange={setTab}
    >
      <div className="space-y-4" data-testid="comp-detail-page">
        {tab === "overview" && (
          <>
            <div className="hv-card p-4 space-y-2 text-sm" data-testid="comp-meta">
              <div className="flex items-center gap-2 flex-wrap">
                <Trophy className="w-4 h-4 text-amber-300" />
                <span className="text-[#9aa3b5] text-xs">
                  {comp.start_date} → {comp.end_date}
                </span>
                <span className="text-[#9aa3b5] text-xs">·</span>
                <span className="text-[#cbd5e1] text-xs">
                  {comp.metric_source === "auto" ? `Auto · ${comp.metric_key}` : "Manual scoring"}
                </span>
                {comp.auto_grant_points && (
                  <span className="px-2 py-0.5 rounded text-[10px] bg-amber-500/20 text-amber-300 flex items-center gap-1">
                    <Coins className="w-3 h-3" /> Auto-grant {comp.payout_first}/{comp.payout_second}/{comp.payout_third}
                  </span>
                )}
              </div>
              {comp.description && <div className="text-[#dde3ec]">{comp.description}</div>}
              {comp.prize && <div className="text-amber-300 text-xs">Prize: {comp.prize}</div>}
            </div>

            {isClosed && Array.isArray(comp.auto_grants) && comp.auto_grants.length > 0 && (
              <div className="hv-card p-4 space-y-1 text-xs" data-testid="comp-auto-grants">
                <div className="font-semibold text-white flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-300" /> Points granted
                </div>
                {comp.auto_grants.map((g) => (
                  <div key={g.user_id} className="text-[#dde3ec]">#{g.rank} — {g.points} pts</div>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "leaderboard" && (
          <div className="hv-card overflow-hidden" data-testid="comp-leaderboard">
            <div className="px-4 py-2.5 border-b border-white/5 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-white">Leaderboard</h2>
              <span className="text-[10px] text-[#647087]">{rows.length} participants</span>
            </div>
            {rows.length === 0 ? (
              <div className="p-6 text-[#9aa3b5] text-sm text-center">No participants yet.</div>
            ) : (
              <ul className="divide-y divide-white/5">
                {rows.map((row) => {
                  const edit = scoreEdits[row.user_id] || { value: row.value ?? "", note: row.note || "" };
                  return (
                    <li
                      key={row.user_id}
                      className="px-3 sm:px-4 py-3"
                      data-testid={`comp-row-${row.user_id}`}
                    >
                      <div className="flex items-center gap-3 flex-wrap">
                        <span
                          className={
                            "w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center " +
                            (row.rank === 1 ? "bg-amber-300 text-black" :
                              row.rank === 2 ? "bg-zinc-300 text-black" :
                              row.rank === 3 ? "bg-amber-700/60 text-amber-100" :
                              "bg-white/10 text-[#dde3ec]")
                          }
                        >
                          {row.rank ?? "—"}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-white truncate">{row.name}</div>
                          <div className="text-[10px] text-[#647087]">{row.role}</div>
                        </div>
                        {!isManual || isClosed ? (
                          <div className="text-sm font-mono text-white">{row.value ?? "—"}</div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <input
                              type="number"
                              step="any"
                              value={edit.value}
                              onChange={(e) =>
                                setScoreEdits({ ...scoreEdits, [row.user_id]: { ...edit, value: e.target.value } })
                              }
                              className="hv-input text-xs w-20 text-right"
                              data-testid={`comp-score-input-${row.user_id}`}
                              disabled={!isHr}
                              placeholder="—"
                            />
                            {isHr && (
                              <button
                                type="button"
                                onClick={() => saveScore(row.user_id)}
                                disabled={savingUid === row.user_id}
                                className="hv-btn hv-btn-ghost text-[10px] px-2"
                                data-testid={`comp-score-save-${row.user_id}`}
                              >
                                {savingUid === row.user_id
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <Save className="w-3 h-3" />}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      {row.note && (
                        <div className="text-[11px] text-[#9aa3b5] mt-1 pl-10">{row.note}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {tab === "activity" && (
          <HistoryBlock entity={{ id, kind: "competition" }} />
        )}
      </div>
    </EntityShell>
  );
}
